import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { notificationsApi } from '../lib/api'

interface Notification {
  id: string
  type: string
  title: string
  message: string
  status: string
  actionUrl?: string | null
  createdAt: string
}

interface Meta {
  total: number
  page: number
  limit: number
  totalPages: number
}

type Filter = 'all' | 'unread'

// Full-page counterpart to NotificationBell.tsx's dropdown -- the dropdown stays a
// quick-glance list capped at 10 with no pagination; this page is where "view and
// manage all notifications" actually happens. Mirrors Browse.tsx's Previous/Page/Next
// pagination footer and Messages.tsx's page layout conventions.
export default function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [meta, setMeta] = useState<Meta | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const fetchNotifications = async (targetPage: number, targetFilter: Filter) => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await notificationsApi.getAll({
        page: targetPage,
        limit: 10,
        ...(targetFilter === 'unread' ? { status: 'UNREAD' } : {}),
      })
      if (res.data.success) {
        setNotifications(res.data.data || [])
        setMeta(res.data.meta || null)
      } else {
        setError(res.data.error || 'Could not load notifications.')
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not load notifications. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchNotifications(page, filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter])

  const changeFilter = (next: Filter) => {
    if (next === filter) return
    setFilter(next)
    setPage(1)
  }

  const goToPage = (next: number) => {
    if (!meta || next < 1 || next > meta.totalPages) return
    setPage(next)
  }

  const handleClick = async (n: Notification) => {
    if (n.status === 'UNREAD') {
      try {
        await notificationsApi.markAsRead(n.id)
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, status: 'READ' } : x)))
      } catch {
        // ignore -- getting the user to the action matters more than this call
      }
    }
    if (n.actionUrl) navigate(n.actionUrl)
  }

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllAsRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, status: 'READ' })))
    } catch {
      // ignore
    }
  }

  const formatTime = (iso: string) => {
    const date = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    const diffHr = Math.floor(diffMin / 60)
    const diffDay = Math.floor(diffHr / 24)
    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHr < 24) return `${diffHr}h ago`
    if (diffDay < 7) return `${diffDay}d ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const hasUnread = notifications.some((n) => n.status === 'UNREAD')

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />

      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="container mx-auto px-4 py-6 max-w-2xl">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[var(--text-primary)]">Notifications</h1>
              <p className="text-[var(--text-secondary)] mt-1">Everything igotthatus.com has let you know about.</p>
            </div>
            {hasUnread && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-sm font-semibold text-[var(--forest-green)] shrink-0 mt-1"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => changeFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filter === 'all'
                  ? 'bg-[var(--forest-green)] text-white'
                  : 'bg-white border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-tint)]'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => changeFilter('unread')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filter === 'unread'
                  ? 'bg-[var(--forest-green)] text-white'
                  : 'bg-white border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-tint)]'
              }`}
            >
              Unread
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-4 rounded-xl mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm">{error}</p>
              <button onClick={() => fetchNotifications(page, filter)} className="btn btn-primary shrink-0">
                Try again
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center text-[var(--text-secondary)]">
              Loading...
            </div>
          ) : notifications.length === 0 && !error ? (
            <div className="bg-white rounded-xl border border-[var(--border)] p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--bg-tint)] flex items-center justify-center mx-auto mb-4">
                <span className="mdi mdi-bell-outline text-2xl text-[var(--forest-green)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                {filter === 'unread' ? "You're all caught up" : 'No notifications yet'}
              </h3>
              <p className="text-[var(--text-secondary)] max-w-md mx-auto">
                {filter === 'unread'
                  ? 'Nothing unread right now.'
                  : "Updates about your rentals and listings will show up here."}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[var(--border)] overflow-hidden">
              {notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleClick(n)}
                  className={`flex items-start gap-3 w-full text-left p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-tint)] transition-colors ${
                    n.status === 'UNREAD' ? 'bg-[var(--bg-tint)]' : 'bg-white'
                  }`}
                >
                  {n.status === 'UNREAD' && (
                    <span className="shrink-0 mt-[7px] w-2 h-2 rounded-full bg-[var(--forest-green)]" />
                  )}
                  <div className={`flex-1 min-w-0 ${n.status !== 'UNREAD' ? 'ml-[14px]' : ''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-semibold text-[var(--text-primary)] text-sm">{n.title}</span>
                      <span className="text-xs text-[var(--text-muted)] shrink-0">{formatTime(n.createdAt)}</span>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] mt-0.5">{n.message}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--border)]">
              <button
                onClick={() => goToPage(meta.page - 1)}
                disabled={meta.page <= 1}
                className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-[var(--text-secondary)]">
                Page {meta.page} of {meta.totalPages}
              </span>
              <button
                onClick={() => goToPage(meta.page + 1)}
                disabled={meta.page >= meta.totalPages}
                className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
